import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bot, Plus, Activity, ShieldOff, Pause, Play, Sparkles, Loader2, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { agentsApi, formatApiError } from "../../lib/ledgerApi";
import AgentKeyRevealCard from "../../components/AgentKeyRevealCard";

const StatusPill = ({ s }) => {
  const map = {
    active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    revoked: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${map[s] || "border-border bg-muted"}`}>
      {s}
    </span>
  );
};

function relativeTime(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 5) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Developer() {
  const nav = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  // Form state
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [perTxCap, setPerTxCap] = useState("50");
  const [dailyCap, setDailyCap] = useState("200");
  const [weeklyCap, setWeeklyCap] = useState("1000");
  const [monthlyCap, setMonthlyCap] = useState("3000");
  const [approvalAbove, setApprovalAbove] = useState("100");
  const [allowlist, setAllowlist] = useState("");
  const [blocklist, setBlocklist] = useState("");
  const [categories, setCategories] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await agentsApi.list();
      setAgents(r);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, []);

  const onCreate = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const splitVPAs = (s) => s.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
      const r = await agentsApi.create({
        display_name: name.trim(),
        description: desc.trim() || null,
        policy: {
          per_tx_cap_usd: perTxCap,
          daily_cap_usd: dailyCap,
          weekly_cap_usd: weeklyCap,
          monthly_cap_usd: monthlyCap,
          require_human_approval_above_usd: approvalAbove,
          merchant_allowlist: splitVPAs(allowlist),
          merchant_blocklist: splitVPAs(blocklist),
          category_allowlist: splitVPAs(categories),
        },
      });
      setCreatedKey(r);
      setCreateOpen(false);
      // Reset form
      setName(""); setDesc("");
      load();
    } catch (e) {
      toast.error("Create failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onTogglePause = async (a) => {
    try {
      if (a.status === "paused") await agentsApi.resume(a.id);
      else await agentsApi.pause(a.id);
      toast.success(a.status === "paused" ? "Resumed" : "Paused");
      load();
    } catch (e) {
      toast.error("Toggle failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  const onRunDemo = async () => {
    setBusy(true);
    try {
      const r = await agentsApi.runScenario();
      toast.success("Scenario complete", {
        description: `${r.steps.length} step(s) — see toast events`,
      });
      r.steps.forEach((s, i) => {
        if (s.error) toast.error(`Step ${s.step}: ${s.error.code || "error"}`);
        else toast.message(`Step ${s.step} ${s.result}`, {
          description: `${s.amount} USD · ${s.receipt_id ? `rcpt ${s.receipt_id.slice(0, 10)}…` : "no receipt"}`,
        });
      });
    } catch (e) {
      toast.error("Demo failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="developer-page">
      <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
            / Developer · Agents
          </div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <Bot className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
            AI agents
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Each agent is a sub-account under your business with its own DID, policy
            envelope, and API keys. Agents settle through the same ledger and fraud
            engine — only the identity changes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={onRunDemo}
            disabled={busy}
            data-testid="run-demo-scenario-btn"
            variant="outline"
            className="rounded-sm"
          >
            <Sparkles className="w-3 h-3 mr-1.5" strokeWidth={1.5} />
            Run demo scenario
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            data-testid="new-agent-btn"
            className="rounded-sm"
          >
            <Plus className="w-3 h-3 mr-1.5" strokeWidth={1.5} /> New agent
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && agents.length === 0 && (
        <div data-testid="agents-empty" className="border border-border rounded-sm bg-card p-10 text-center">
          <Bot className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-sm font-medium mb-1">No agents yet</div>
          <div className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">
            Create an agent to mint API keys, set spending policy, and let it pay other
            businesses (or merchant APIs returning HTTP 402) on your behalf.
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-sm">Create your first agent</Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((a) => (
          <Link
            key={a.id}
            to={`/app/developer/${a.id}`}
            data-testid={`agent-card-${a.id}`}
            className="border border-border rounded-sm bg-card p-5 hover:border-foreground transition-colors"
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Bot className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  <div className="text-sm font-medium truncate">{a.display_name}</div>
                </div>
                <div className="font-mono text-[10px] text-muted-foreground truncate" title={a.did}>
                  {a.did}
                </div>
              </div>
              <StatusPill s={a.status} />
            </div>
            {a.description && (
              <div className="text-xs text-muted-foreground mb-3 line-clamp-2">{a.description}</div>
            )}
            <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Per tx</div>
                <div>${a.policy.per_tx_cap_usd}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Daily</div>
                <div>${a.policy.daily_cap_usd}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Approve&gt;</div>
                <div>${a.policy.require_human_approval_above_usd}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
              <button
                onClick={(e) => { e.preventDefault(); onTogglePause(a); }}
                disabled={a.status === "revoked"}
                data-testid={`agent-pause-${a.id}`}
                className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-40"
              >
                {a.status === "paused"
                  ? <><Play className="w-3 h-3" strokeWidth={1.5} /> Resume</>
                  : <><Pause className="w-3 h-3" strokeWidth={1.5} /> Pause</>}
              </button>
              <span
                data-testid={`agent-last-active-${a.id}`}
                className="text-[10px] text-muted-foreground inline-flex items-center gap-1 font-mono"
                title={a.last_active_at || "never"}
              >
                <Activity className="w-3 h-3" strokeWidth={1.5} />
                {relativeTime(a.last_active_at)}
              </span>
            </div>
          </Link>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="agent-create-dialog" className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-medium">New agent</DialogTitle>
            <DialogDescription>
              Set a spending policy. The agent's API key will be shown ONCE after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Display name</Label>
              <Input
                data-testid="agent-name-input"
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="ResearchBot"
                className="rounded-sm font-mono mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Textarea
                data-testid="agent-desc-input"
                value={desc} onChange={(e) => setDesc(e.target.value)}
                rows={2}
                placeholder="What does this agent do?"
                className="rounded-sm mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Per-tx cap (USD)</Label>
                <Input data-testid="policy-per-tx" value={perTxCap} onChange={(e) => setPerTxCap(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Approve above (USD)</Label>
                <Input data-testid="policy-approval-above" value={approvalAbove} onChange={(e) => setApprovalAbove(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Daily cap (USD)</Label>
                <Input data-testid="policy-daily" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Weekly cap (USD)</Label>
                <Input data-testid="policy-weekly" value={weeklyCap} onChange={(e) => setWeeklyCap(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Monthly cap (USD)</Label>
                <Input data-testid="policy-monthly" value={monthlyCap} onChange={(e) => setMonthlyCap(e.target.value)} className="rounded-sm font-mono mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Merchant allowlist (VPAs)</Label>
                <Input data-testid="policy-allow" value={allowlist} onChange={(e) => setAllowlist(e.target.value)} placeholder="globex@neopay, initech@neopay" className="rounded-sm font-mono mt-1" />
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">empty = any merchant</div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Merchant blocklist</Label>
                <Input data-testid="policy-block" value={blocklist} onChange={(e) => setBlocklist(e.target.value)} placeholder="bad@neopay" className="rounded-sm font-mono mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Category allowlist</Label>
                <Input data-testid="policy-categories" value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="data, compute, api-calls" className="rounded-sm font-mono mt-1" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={onCreate} disabled={busy || !name.trim()} data-testid="agent-create-submit" className="rounded-sm">
              {busy ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Plus className="w-3 h-3 mr-1.5" strokeWidth={1.5} />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {createdKey && (
        <AgentKeyRevealCard
          data={createdKey}
          onClose={() => setCreatedKey(null)}
          onView={() => { setCreatedKey(null); nav(`/app/developer/${createdKey.agent.id}`); }}
        />
      )}
    </div>
  );
}
